import { array, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce, next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupData, PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SearchableListItemComponent from 'webapp/components/searchable-list-item';

import type PopupContext from 'webapp/models-custom/popup/context';

const DEFAULT_MAX_VISIBLE_ITEMS = 3;

interface Args extends PopupData.Common.List {
	maxVisibleItems?: number;
	onMoreOpen?: () => void;
	// Forwarded to each item so labels can be shown alongside icons (icon-only by default)
	showTextProperty?: keyof PopupListItem<string>;
}

export default class CommonCollapsiblePillOptionsComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	@tracked contextOpened = false;
	@tracked showAll = true;
	@tracked visibleItems: PopupListItem<string>[] = [];

	node: HTMLElement | null = null;
	popup: PopupContext | null = null;
	resizeObserver: ResizeObserver | null = null;
	recalculating = false;
	ctnWidth = 0;
	lastCalculatedWidth = 0;
	initialized = true;

	get hiddenItems() {
		return this.args.itemsList.slice(this.visibleItems.length, this.args.itemsList.length);
	}

	get showContextAction() {
		return this.showAll || !!this.hiddenItems.length;
	}

	get itemListVisibilityStates() {
		return this.args.itemsList.map((_item, index) =>
			index < this.visibleItems.length ? 'visible' : 'collapsed',
		);
	}

	isVisible(el: HTMLElement, cnt: HTMLElement) {
		const elLeft = el.offsetLeft;
		const elRight = elLeft + el.clientWidth;

		const cntLeft = cnt.scrollLeft;
		const cntRight = cntLeft + cnt.clientWidth;

		return elLeft >= cntLeft && elRight <= cntRight; // The element is fully visible in the container
	}

	updateVisibleItems(maxVisibleItemsLength: number) {
		// If only 1 item would be hidden in the "more" dropdown, show it directly instead
		if (this.args.itemsList.length - maxVisibleItemsLength === 1) {
			maxVisibleItemsLength = this.args.itemsList.length;
		}

		this.showAll = false;
		this.visibleItems = this.args.itemsList.slice(0, maxVisibleItemsLength);
		const node = this.node!;

		void this.popup?.close();

		next(() => {
			// because this.visibleItems was updated
			if (maxVisibleItemsLength && this.args.itemsList.length !== maxVisibleItemsLength) {
				const moreEl = node.querySelector<HTMLElement>('.pill-option-more')!;

				if (this.isVisible(moreEl, node)) {
					this.recalculating = false;
				} else {
					this.updateVisibleItems(maxVisibleItemsLength - 1);
				}
			} else {
				this.recalculating = false;
			}
		});
	}

	debouncedResizeHandler() {
		if (!this.initialized && !this.recalculating) {
			this.recalculate();
		}

		this.initialized = false; // skip calling recalculate the first time, cause setupView called it on the leading end of the debounce
	}

	observerHandler = (entries: ResizeObserverEntry[]) => {
		for (const entry of entries) {
			const width = entry.borderBoxSize?.[0]!.inlineSize;
			if (typeof width === 'number' && width !== this.ctnWidth) {
				this.ctnWidth = width;
				debounce(this, this.debouncedResizeHandler, 1000, false);
			}
		}
	};

	bindObserver = () => {
		if (this.node) {
			this.resizeObserver = new ResizeObserver(this.observerHandler);
			this.resizeObserver.observe(this.node, { box: 'border-box' });
		}
	};

	unbindObserver = () => {
		if (this.node) {
			this.resizeObserver?.unobserve(this.node);
		}
	};

	@action
	recalculate(): void {
		const node = this.node;

		if (node) {
			const currentWidth = Math.round(node.clientWidth);

			if (this.lastCalculatedWidth && currentWidth === this.lastCalculatedWidth) {
				return;
			}

			this.lastCalculatedWidth = currentWidth;
			this.recalculating = true;
			this.showAll = true;

			const staticEl = node.firstElementChild as HTMLElement;
			staticEl.style.opacity = '0';

			next(() => {
				// because this.recalculating was updated
				this.visibleItems = [];

				let lastVisibleIndex = '',
					maxVisibleItemsLength = 0;

				const ulEl = staticEl.firstElementChild as HTMLElement;

				[...ulEl.children].forEach((tagEl) => {
					const dataExtra = tagEl.getAttribute('data-extra')!; // 'more' is always visible because it's moved at the beginning of the elemet with flex order

					if (dataExtra !== 'more' && this.isVisible(tagEl as HTMLElement, node)) {
						lastVisibleIndex = dataExtra;
					}
				});

				const maxVisible = this.args.maxVisibleItems ?? DEFAULT_MAX_VISIBLE_ITEMS;
				maxVisibleItemsLength = lastVisibleIndex
					? Math.min(maxVisible, 1 + Number(lastVisibleIndex.split(' ')[0]))
					: 0;
				staticEl.style.opacity = '1';
				this.updateVisibleItems(maxVisibleItemsLength);
			});
		}
	}

	@action
	triggerAction(item: PopupListItem<string>) {
		if (!item.isDisabled) {
			/*
				item.action doesn't have to be set to call doAction (example redirectToLoginUrl in app\components\account\popup-spaces.ts which only uses item.id)
				so `if (!item.isDisabled && item.action) {` would break it
			*/

			this.doAction(item.action, item.id);
		}
	}

	@action
	doAction(actionName?: string, itemId?: string | number | null) {
		this.args.doAction(actionName, itemId);
	}

	@action
	openContextDropdown(e: PointerEvent): void {
		if (this.contextOpened) {
			return;
		}

		const attachTo = e.target as HTMLElement,
			popup = this.popupService.createListPopup(attachTo, undefined, undefined, true),
			hiddenItems = this.hiddenItems;

		this.popup = popup || null;

		if (popup) {
			popup.open({
				itemsList: hiddenItems,
				doAction: (actionName: string, itemId: string) => {
					this.doAction(actionName, itemId);
				},
			});

			this.contextOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.contextOpened = false;

					this.popup = null;
				}
			});

			this.args.onMoreOpen?.();
		}
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
		this.recalculate();
		this.bindObserver();
	}

	@action
	teardownView(): void {
		this.unbindObserver();
	}
	<template>
		<div
			class={{joinClasses
				(array
					"dbx-ui-collapsible-pill-options"
					(if this.showAll "all")
					(if @showTextProperty "with-labels")
				)
			}}
			{{didInsert this.setupView}}
			{{didUpdate this.recalculate @itemsList}}
			{{willDestroy this.teardownView}}
		>
			<div class="non-static">
				<ul class="pill-options-links">
					{{#each @itemsList as |item index|}}
						<SearchableListItemComponent
							@item={{item}}
							@showTextProperty={{@showTextProperty}}
							@onTriggerAction={{this.triggerAction}}
							data-extra="{{index}} {{get this.itemListVisibilityStates index}}"
						/>
					{{/each}}
					{{#if this.showContextAction}}
						<li
							class={{joinClasses
								(array "pill-option-more" (if this.contextOpened "open"))
							}}
							data-extra="more"
						>
							<a role="button" {{on "click" this.openContextDropdown}}>
								<CommonUiIconComponent @name="Ellipsis" @size="md" />
							</a>
						</li>
					{{/if}}
				</ul>
			</div>
		</div>
	</template>
}
